import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, RefreshCw, Package, DollarSign, Clock, ShoppingBag, RotateCcw, Loader2, ChevronDown, Palette, Megaphone, Plus, Trash2, Save } from 'lucide-react';
import {
  adminMe, clearAdminToken, getAdminToken, fetchAdminOrders, fetchAdminStats,
  updateOrderStatus, refundOrder, imgUrl, adminGetAnnouncements, adminSaveAnnouncements,
} from '../lib/api';
import AdminAnalytics from '../components/AdminAnalytics';
import SaleScheduler from '../components/SaleScheduler';
import BannerStyleControl from '../components/BannerStyleControl';
import PromoManager from '../components/PromoManager';

const STATUS_OPTIONS = ['paid', 'fulfilled', 'shipped', 'delivered', 'cancelled'];

const STATUS_STYLES = {
  pending: 'bg-[#3a3320] text-[#e7d3a1] border-[#c6a15b]/40',
  paid: 'bg-[#1f3a28] text-[#8fe0a6] border-[#4a8c5b]/50',
  fulfilled: 'bg-[#1f2f3a] text-[#8fc7e0] border-[#4a7c8c]/50',
  shipped: 'bg-[#26223a] text-[#b3a6e0] border-[#6a5c8c]/50',
  delivered: 'bg-[#1f3a28] text-[#8fe0a6] border-[#4a8c5b]/50',
  cancelled: 'bg-[#3a2222] text-[#e0a6a6] border-[#8c4a4a]/50',
  refunded: 'bg-[#3a2f22] text-[#e0c2a6] border-[#8c6a4a]/50',
  failed: 'bg-[#3a2222] text-[#e0a6a6] border-[#8c4a4a]/50',
};

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return iso; }
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-[#141311] border border-[#c6a15b]/20 p-6" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <p className="overline text-[0.6rem] text-[#9c9587]">{label}</p>
      <Icon size={18} className="text-[#c6a15b]" />
    </div>
    <p className="font-display text-3xl text-[#f7f3ec]">{value}</p>
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [busyId, setBusyId] = useState('');
  const [toast, setToast] = useState('');
  const [announcements, setAnnouncements] = useState([]);
  const [annSaving, setAnnSaving] = useState(false);

  const logout = useCallback(() => { clearAdminToken(); navigate('/admin/login'); }, [navigate]);

  const load = useCallback(async () => {
    try {
      const [o, s, a] = await Promise.all([fetchAdminOrders(), fetchAdminStats(), adminGetAnnouncements()]);
      setOrders(o);
      setStats(s);
      setAnnouncements(a.items || []);
    } catch { logout(); }
  }, [logout]);

  useEffect(() => {
    if (!getAdminToken()) { navigate('/admin/login'); return; }
    adminMe().then(async () => { await load(); setReady(true); }).catch(() => logout());
  }, [navigate, load, logout]);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const saveAnnouncements = async () => {
    const items = announcements.map((t) => t.trim()).filter(Boolean);
    if (!items.length) { flash('Add at least one message'); return; }
    setAnnSaving(true);
    try {
      const res = await adminSaveAnnouncements(items);
      setAnnouncements(res.items);
      flash('Announcement bar updated');
    } catch (e) { flash(e.message); }
    setAnnSaving(false);
  };

  const onStatusChange = async (orderId, status) => {
    setBusyId(orderId);
    try {
      const updated = await updateOrderStatus(orderId, status);
      setOrders((prev) => prev.map((o) => (o.order_id === orderId ? updated : o)));
      flash(`Order updated to ${status}`);
      fetchAdminStats().then(setStats).catch(() => {});
    } catch (e) { flash(e.message); }
    setBusyId('');
  };

  const onRefund = async (orderId) => {
    if (!window.confirm('Refund this order? This will issue a Stripe refund to the customer.')) return;
    setBusyId(orderId);
    try {
      const updated = await refundOrder(orderId);
      setOrders((prev) => prev.map((o) => (o.order_id === orderId ? updated : o)));
      flash('Order refunded');
      fetchAdminStats().then(setStats).catch(() => {});
    } catch (e) { flash(e.message); }
    setBusyId('');
  };

  const sortedOrders = useMemo(() => orders, [orders]);

  if (!ready) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-[#0b0b0b]">
        <Loader2 size={34} className="text-[#c6a15b] animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-[#0b0b0b] min-h-screen" data-testid="admin-dashboard-page">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-10">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <p className="overline text-gold-gradient mb-1">Admin</p>
            <h1 className="font-display text-4xl sm:text-5xl text-[#f7f3ec]">Order Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} className="flex items-center gap-2 px-4 py-2.5 border border-[#c6a15b]/40 text-[#e7d3a1] overline text-[0.6rem] hover:bg-white/5 transition-colors" data-testid="admin-refresh-btn">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={logout} className="flex items-center gap-2 px-4 py-2.5 border border-[#8c4a4a]/50 text-[#e0a6a6] overline text-[0.6rem] hover:bg-[#3a2222] transition-colors" data-testid="admin-logout-btn">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <StatCard icon={ShoppingBag} label="Total Orders" value={stats.total_orders} testid="stat-total" />
            <StatCard icon={Package} label="Paid Orders" value={stats.paid_orders} testid="stat-paid" />
            <StatCard icon={DollarSign} label="Revenue (items)" value={money(stats.revenue_cents)} testid="stat-revenue" />
            <StatCard icon={Clock} label="Pending" value={stats.pending_orders} testid="stat-pending" />
          </div>
        )}

        {/* Analytics: best-sellers + revenue trends */}
        <AdminAnalytics />

        {/* Sale scheduler + banner style + promo codes */}
        <SaleScheduler flash={flash} />
        <BannerStyleControl flash={flash} />
        <PromoManager flash={flash} />

        {/* Announcement bar editor */}
        <div className="bg-[#141311] border border-[#c6a15b]/20 p-6 mb-10" data-testid="announcements-editor">
          <div className="flex items-center gap-2 mb-4">
            <Megaphone size={18} className="text-[#c6a15b]" />
            <h2 className="font-display text-2xl text-[#f7f3ec]">Announcement Bar</h2>
          </div>
          <p className="font-body text-xs text-[#9c9587] mb-4">These messages rotate across the top of your site. Add, edit or remove them.</p>
          <div className="space-y-3">
            {announcements.map((msg, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  value={msg}
                  onChange={(e) => setAnnouncements((prev) => prev.map((m, i) => (i === idx ? e.target.value : m)))}
                  data-testid={`announcement-input-${idx}`}
                  className="flex-1 bg-[#0b0b0b] border border-[#c6a15b]/30 px-4 py-2.5 font-body text-sm text-[#f7f3ec] outline-none focus:border-[#c6a15b]" />
                <button onClick={() => setAnnouncements((prev) => prev.filter((_, i) => i !== idx))}
                  data-testid={`announcement-remove-${idx}`}
                  className="p-2.5 border border-[#8c4a4a]/50 text-[#e0a6a6] hover:bg-[#3a2222] transition-colors">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <button onClick={() => setAnnouncements((prev) => [...prev, ''])}
              data-testid="announcement-add-btn"
              className="flex items-center gap-2 px-4 py-2.5 border border-[#c6a15b]/40 text-[#e7d3a1] overline text-[0.6rem] hover:bg-white/5 transition-colors">
              <Plus size={14} /> Add Message
            </button>
            <button onClick={saveAnnouncements} disabled={annSaving}
              data-testid="announcement-save-btn"
              className="btn-gold flex items-center gap-2 px-6 py-2.5 disabled:opacity-60">
              <span className="flex items-center gap-2">{annSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</span>
            </button>
          </div>
        </div>

        {/* Orders */}
        {sortedOrders.length === 0 ? (
          <div className="bg-[#141311] border border-[#c6a15b]/20 p-16 text-center">
            <ShoppingBag size={40} className="text-[#c6a15b] mx-auto mb-4" />
            <p className="font-display text-2xl text-[#f7f3ec]">No orders yet</p>
            <p className="font-body text-sm text-[#9c9587] mt-2">Orders will appear here as customers check out.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-orders-list">
            {sortedOrders.map((o) => {
              const busy = busyId === o.order_id;
              const refunded = o.status === 'refunded';
              const isPaid = o.payment_status === 'paid' || ['paid', 'fulfilled', 'shipped', 'delivered'].includes(o.status);
              const hasCustom = o.items?.some((it) => (it.color || '').toLowerCase().startsWith('custom'));
              return (
                <div key={o.order_id} className={`bg-[#141311] border p-5 sm:p-6 ${hasCustom ? 'border-[#c6a15b] border-l-4' : 'border-[#c6a15b]/20'}`} data-testid={`order-row-${o.order_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="font-body text-xs text-[#7a7365]">#{o.order_id?.slice(0, 8)} · {fmtDate(o.created_at)}</p>
                      <p className="font-display text-2xl text-[#f7f3ec] mt-1">{money(o.amount_subtotal)}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span className={`inline-block overline text-[0.55rem] px-2.5 py-1 border ${STATUS_STYLES[o.status] || STATUS_STYLES.pending}`} data-testid={`order-status-${o.order_id}`}>
                          {o.status}
                        </span>
                        {hasCustom && (
                          <span className="inline-flex items-center gap-1.5 overline text-[0.55rem] px-2.5 py-1 border bg-[#3a3320] text-[#e7d3a1] border-[#c6a15b]" data-testid={`order-custom-flag-${o.order_id}`}>
                            <Palette size={12} /> Custom Color · needs extra time
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="relative">
                        <select
                          value={STATUS_OPTIONS.includes(o.status) ? o.status : ''}
                          onChange={(e) => onStatusChange(o.order_id, e.target.value)}
                          disabled={busy || refunded}
                          data-testid={`order-status-select-${o.order_id}`}
                          className="appearance-none bg-[#0b0b0b] border border-[#c6a15b]/40 text-[#e7d3a1] font-body text-sm pl-4 pr-9 py-2.5 outline-none focus:border-[#c6a15b] disabled:opacity-50 cursor-pointer">
                          <option value="" disabled>Set status…</option>
                          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <ChevronDown size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#c6a15b] pointer-events-none" />
                      </div>
                      <button
                        onClick={() => onRefund(o.order_id)}
                        disabled={busy || refunded || !isPaid}
                        data-testid={`order-refund-btn-${o.order_id}`}
                        className="flex items-center gap-2 px-4 py-2.5 border border-[#8c6a4a]/50 text-[#e0c2a6] overline text-[0.6rem] hover:bg-[#3a2f22] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                        {busy ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} {refunded ? 'Refunded' : 'Refund'}
                      </button>
                    </div>
                  </div>

                  {/* Items */}
                  <div className="mt-5 pt-5 border-t border-white/10 grid gap-3">
                    {o.items?.map((it, i) => (
                      <div key={i} className="flex items-center gap-3">
                        {it.image && <img src={imgUrl(it.image)} alt={it.name} className="w-11 h-14 object-cover bg-[#0b0b0b]" />}
                        <div className="flex-1 min-w-0">
                          <p className="font-body text-sm text-[#f7f3ec] truncate">{it.name}</p>
                          <p className="font-body text-xs text-[#9c9587]">
                            {[it.length, it.color].filter(Boolean).join(' · ')}{it.length || it.color ? ' · ' : ''}Qty {it.quantity}
                          </p>
                        </div>
                        <span className="font-body text-sm text-[#e7d3a1]">{money((it.unit_amount || 0) * it.quantity)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#141311] border border-[#c6a15b]/50 text-[#e7d3a1] font-body text-sm px-6 py-3 shadow-xl z-[80]" data-testid="admin-toast">
          {toast}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
